import { NavLink, Link, useNavigate } from "react-router-dom";
import { House, Package, MapPin, Tag, SignOut, MagnifyingGlass, Sparkle, UserCircle, Globe, Article, Question, BookOpen, EnvelopeSimple, PencilLine, FlagBanner, Users } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", Icon: House, testid: "nav-home" },
  { to: "/items", label: "Items", Icon: Package, testid: "nav-items" },
  { to: "/locations", label: "Locations", Icon: MapPin, testid: "nav-locations" },
  { to: "/categories", label: "Categories", Icon: Tag, testid: "nav-categories" },
  { to: "/search", label: "Search", Icon: MagnifyingGlass, testid: "nav-search" },
  { to: "/profile", label: "Profile", Icon: UserCircle, testid: "nav-profile" },
  { to: "/pricing", label: "Plans", Icon: Sparkle, testid: "nav-pricing" },
];

const publicLinks = [
  { to: "/landing", label: "Landing page", Icon: Globe, testid: "nav-public-landing" },
  { to: "/blog", label: "Blog", Icon: Article, testid: "nav-public-blog" },
  { to: "/about", label: "About", Icon: BookOpen, testid: "nav-public-about" },
  { to: "/faq", label: "FAQ", Icon: Question, testid: "nav-public-faq" },
  { to: "/contact", label: "Contact", Icon: EnvelopeSimple, testid: "nav-public-contact" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-sand-100">
      {/* Topbar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-sand-100/80 border-b border-sand-300">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient">
              <House weight="duotone" size={22} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold text-stoneink-900 leading-none">Nestled</div>
              <div className="text-xs text-stoneink-500 mt-0.5">a place for everything</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/profile"
              data-testid="profile-link"
              className="hidden sm:flex items-center gap-3 px-2 sm:px-3 h-12 rounded-xl hover:bg-sand-200 transition-colors"
              title="Account settings"
            >
              <div className="text-right">
                <div className="text-sm font-medium text-stoneink-900" data-testid="user-name">{user?.name}</div>
                <div className="text-xs text-stoneink-500">{user?.email}</div>
              </div>
              <div className="h-9 w-9 rounded-full bg-sand-200 text-stoneink-700 grid place-items-center font-heading font-semibold">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
            </Link>
            {user?.tier && user.tier !== "free" ? (
              <Link to="/pricing" data-testid="tier-badge"
                className={`h-10 px-3 rounded-xl text-xs font-bold uppercase tracking-[0.16em] flex items-center gap-1.5 ${
                  user.tier === "family" ? "bg-ochre-400/30 text-ochre-600" : "bg-terracotta-500 text-white"
                }`}>
                <Sparkle weight="fill" size={12} /> {user.tier}
              </Link>
            ) : (
              <Link to="/pricing" data-testid="upgrade-cta"
                className="hidden sm:flex h-10 px-3 rounded-xl bg-terracotta-50 text-terracotta-600 hover:bg-terracotta-100 text-sm font-medium items-center gap-1.5 transition-colors">
                <Sparkle weight="fill" size={14} /> Upgrade
              </Link>
            )}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="h-10 px-4 rounded-xl bg-white border border-sand-300 hover:border-ochre-500 hover:bg-sand-100 transition-colors flex items-center gap-2 text-stoneink-700"
            >
              <SignOut size={18} /> <span className="hidden sm:inline text-sm">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Side nav */}
        <nav className="lg:sticky lg:top-24 h-fit">
          <ul className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {links.map(({ to, label, Icon, testid }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === "/"}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all whitespace-nowrap ${
                      isActive
                        ? "bg-terracotta-500 text-white shadow-ambient"
                        : "text-stoneink-700 hover:bg-sand-200"
                    }`
                  }
                >
                  <Icon weight="duotone" size={20} />
                  <span className="text-sm font-medium">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Public site links — visible on lg+ only (the horizontal scrolling row above gets too crowded on mobile) */}
          <div className="hidden lg:block mt-8">
            <div className="px-4 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stoneink-500">
              Public site
            </div>
            <ul className="flex flex-col gap-1">
              {publicLinks.map(({ to, label, Icon, testid }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    data-testid={testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                        isActive
                          ? "bg-sand-200 text-stoneink-900"
                          : "text-stoneink-500 hover:text-stoneink-900 hover:bg-sand-200"
                      }`
                    }
                  >
                    <Icon weight="duotone" size={18} />
                    <span className="text-sm font-medium">{label}</span>
                  </NavLink>
                </li>
              ))}
              {user?.role === "admin" && (
                <>
                  <li>
                    <NavLink
                      to="/admin/blog"
                      data-testid="nav-admin-blog"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all border ${
                          isActive
                            ? "bg-terracotta-50 border-terracotta-500 text-terracotta-600"
                            : "border-transparent text-stoneink-700 hover:bg-sand-200 hover:border-sand-300"
                        }`
                      }
                    >
                      <PencilLine weight="duotone" size={18} />
                      <span className="text-sm font-medium">Manage blog</span>
                      <span className="ml-auto text-[9px] font-bold uppercase tracking-[0.14em] text-terracotta-600">Admin</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/admin/beta"
                      data-testid="nav-admin-beta"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all border ${
                          isActive
                            ? "bg-terracotta-50 border-terracotta-500 text-terracotta-600"
                            : "border-transparent text-stoneink-700 hover:bg-sand-200 hover:border-sand-300"
                        }`
                      }
                    >
                      <FlagBanner weight="duotone" size={18} />
                      <span className="text-sm font-medium">Beta testers</span>
                      <span className="ml-auto text-[9px] font-bold uppercase tracking-[0.14em] text-terracotta-600">Admin</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/admin/users"
                      data-testid="nav-admin-users"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all border ${
                          isActive
                            ? "bg-terracotta-50 border-terracotta-500 text-terracotta-600"
                            : "border-transparent text-stoneink-700 hover:bg-sand-200 hover:border-sand-300"
                        }`
                      }
                    >
                      <Users weight="duotone" size={18} />
                      <span className="text-sm font-medium">All users</span>
                      <span className="ml-auto text-[9px] font-bold uppercase tracking-[0.14em] text-terracotta-600">Admin</span>
                    </NavLink>
                  </li>
                </>
              )}
            </ul>
          </div>
        </nav>

        <main>{children}</main>
      </div>
    </div>
  );
}
